import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { ConsultingLinks } from "@/components/ConsultingExpertise";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { AuthorCard } from "@/components/AuthorCard";
import { ArrowRight, ArrowUpRight, Award, Lightbulb, Layers, Target } from "lucide-react";
import { SOCIAL } from "@/lib/site";


import portraitAvifSrcset from "@/assets/arif-khan-primary.jpg?w=480;720;1080&format=avif&as=srcset";
import portraitWebpSrcset from "@/assets/arif-khan-primary.jpg?w=480;720;1080&format=webp&as=srcset";
import blueprint from "@/assets/blueprint.jpg";
import { hreflangLinks } from "@/lib/hreflang";
import {
  pageSchemaScript,
  PERSON_ID,
  PORTRAIT_ID,
  ALTIMA_URL,
  PRIMARY_PORTRAIT_PATH,
  PRIMARY_PORTRAIT_URL,
  PRIMARY_PORTRAIT_WIDTH,
  PRIMARY_PORTRAIT_HEIGHT,
  PRIMARY_PORTRAIT_ALT,
} from "@/seo/schema";


const ABOUT_TITLE = "About Arif Khan — 23+ Years in Building Technology";
const ABOUT_DESC =
  "Journey, philosophy and capabilities of Arif Khan — BU Head at Altima Global. Authority on security, smart automation and luxury AV.";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () => ({
    meta: [
      { title: ABOUT_TITLE },
      { name: "description", content: ABOUT_DESC },

      { property: "og:title", content: "About Arif Khan" },
      { property: "og:description", content: "Journey, philosophy, capabilities, and recognition." },
      { property: "og:url", content: "https://arifkhanglobal.com/about" },
      { property: "og:image", content: PRIMARY_PORTRAIT_URL },
      { property: "og:image:width", content: String(PRIMARY_PORTRAIT_WIDTH) },
      { property: "og:image:height", content: String(PRIMARY_PORTRAIT_HEIGHT) },
      { property: "og:image:alt", content: "Arif Khan, Security Infrastructure and Building Technology Strategist at Altima Global." },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: PRIMARY_PORTRAIT_URL },
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/about" },
      ...hreflangLinks("/about"),
      { rel: "alternate", type: "text/markdown", href: "https://arifkhanglobal.com/about.md", title: "Markdown mirror" },
    ],
    scripts: [
      pageSchemaScript({
        path: "/about",
        type: "ProfilePage",
        name: ABOUT_TITLE,
        description: ABOUT_DESC,
        breadcrumb: [{ name: "About", url: "/about" }],
        extra: {
          mainEntity: { "@id": PERSON_ID },
          about: { "@id": PERSON_ID },
          primaryImageOfPage: { "@id": PORTRAIT_ID },
        },
      }),
    ],


  }),
});

const TIMELINE = [
  { year: "2001", title: "Entered the AV & security industry", body: "Hands-on system integration and field engineering across India's emerging commercial real estate." },
  { year: "2008", title: "Specialised in perimeter & critical infrastructure", body: "Led multi-kilometre projects for industrial and government sites — defining vendor-neutral design as a discipline." },
  { year: "2015", title: "Scaled smart automation practice", body: "Designed integrated automation programs for luxury residences, corporates and hospitality flagships." },
  { year: "2020", title: "Luxury home cinema & auditorium AV", body: "Built a reference body of acoustically engineered private theatres and institutional AV installations." },
  { year: "2024", title: "BU Head, Altima Global", body: "Leading strategic building-technology consulting at scale — advising owners, architects, and operators." },
];

const PILLARS = [
  { Icon: Target, title: "Strategy First", body: "Every brief begins with the operational and risk model — not the product catalogue." },
  { Icon: Layers, title: "Vendor-Neutral Design", body: "The right tool for the building, not the right relationship for the integrator." },
  { Icon: Lightbulb, title: "Invisible Technology", body: "Systems must perform absolutely and recede into the architecture." },
  { Icon: Award, title: "Lifetime Accountability", body: "A 23-year track record is built one delivered project at a time." },
];

const LESSONS = [
  { n: "01", title: "Plan the building, not the product.", body: "OEMs come and go. A building lives for decades. Every decision is made against the building's operational life — not the brochure on the table." },
  { n: "02", title: "Early coordination is cheaper than late genius.", body: "Most expensive site changes started as a 15-minute conversation that didn't happen at design stage. Coordinate before drawings are frozen." },
  { n: "03", title: "Vendor-neutral is a discipline, not a slogan.", body: "Specifications are written against outcomes — lux levels, intelligibility, retention, integration — and validated against multiple OEMs before commitment." },
  { n: "04", title: "Serviceability is part of the design.", body: "Rack location, access panels, spare conduits, cable labelling and operator simplicity are non-negotiable. If a system can't be serviced, it won't be serviced." },
  { n: "05", title: "Invisible technology is the highest craft.", body: "The best security disappears. The best automation anticipates. The best home cinema vanishes the moment the lights dim. Visibility is a design failure." },
  { n: "06", title: "Owners want clarity, not jargon.", body: "Brief, options, trade-offs, decision. Architects, PMCs and clients deserve a partner who explains the engineering — not one who hides behind it." },
  { n: "07", title: "Confidentiality is a credential.", body: "Project drawings, client identities and site details are protected by default. References are shared selectively and only with permission." },
  { n: "08", title: "The next decade belongs to integration.", body: "Edge-AI, unified control fabric and intelligent buildings will reward owners who planned systems to talk to each other — and punish those who didn't." },
];

function AboutPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="About"
        title={<>A career engineered for the world's most demanding buildings.</>}
        description="Arif Khan is a Security Infrastructure & Building Technology Strategist with 23+ years across perimeter protection, smart automation, luxury home cinema and auditorium AV — currently BU Head at Altima Global."
      />

      {/* Portrait + philosophy */}
      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-border shadow-[0_40px_80px_-40px_rgba(21,101,192,0.35)]">
              <picture>
                <source type="image/avif" srcSet={portraitAvifSrcset} sizes="(min-width: 1024px) 40vw, (min-width: 640px) 60vw, 90vw" />
                <source type="image/webp" srcSet={portraitWebpSrcset} sizes="(min-width: 1024px) 40vw, (min-width: 640px) 60vw, 90vw" />
                <img
                  src={PRIMARY_PORTRAIT_PATH}
                  alt={PRIMARY_PORTRAIT_ALT}
                  width={PRIMARY_PORTRAIT_WIDTH}
                  height={PRIMARY_PORTRAIT_HEIGHT}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover object-[62%_30%]"
                />
              </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
              </div>
              {/* Floating credential card */}
              <div className="absolute -bottom-6 -right-4 md:-right-6 glass rounded-2xl px-5 py-4 max-w-[220px]">
                <div className="eyebrow">BU Head</div>
                <div className="mt-1 text-sm font-semibold leading-tight">Altima Global · New Delhi</div>
                <div className="mt-1 text-xs text-ink-soft">23+ years · 1000+ projects</div>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pt-6">
            <div className="mb-12 border-l-2 border-primary/40 pl-5">
              <h2 className="text-2xl font-semibold tracking-tight">Arif Khan</h2>
              <p className="mt-1 text-ink-soft">Security Infrastructure &amp; Building Technology Strategist</p>
              <p className="mt-1 text-ink-soft">
                BU Head, Security &amp; Automation,{" "}
                <a href={ALTIMA_URL} target="_blank" rel="noopener" className="font-medium text-foreground underline-offset-2 hover:underline">Altima Global</a>
              </p>
              <p className="mt-1 text-sm text-ink-soft">New Delhi, India</p>
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener me" className="tap-target mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-foreground">
                View Arif Khan's LinkedIn profile <ArrowUpRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
            <SectionHeader eyebrow="Philosophy" title={<>Technology should improve experience without becoming visible.</>} />
            <div className="mt-8 space-y-6 text-lg text-ink-soft leading-relaxed">
              <p>The best security is the one no one notices. The best automation anticipates instead of asks. The best home cinema disappears the moment the lights dim.</p>
              <p>For two decades this has been the operating principle behind every design — from kilometre-scale perimeter grids to acoustically perfect private theatres across New Delhi, Noida, Gurugram, Faridabad, Ghaziabad, North India, Mumbai and Bengaluru.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline with blueprint backdrop */}
      <section className="relative py-24 border-y border-border overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={blueprint} alt="" aria-hidden width={1920} height={1024} loading="lazy" decoding="async" className="h-full w-full object-cover opacity-[0.18]" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/85 to-background/95" />
        </div>
        <div className="container-page">
          <SectionHeader eyebrow="My Journey" index="01" title="Two decades, one trajectory." />
          <div className="mt-16 relative">
            <div className="absolute left-4 md:left-[16.5%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent" aria-hidden />
            <div className="space-y-4">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 0.05}>
                  <div className="relative glass rounded-2xl p-7 grid md:grid-cols-12 gap-6 items-start">
                    <div className="md:col-span-2 flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_0_6px_color-mix(in_oklab,var(--primary)_18%,transparent)]" />
                      <span className="font-mono text-sm font-semibold text-primary">{t.year}</span>
                    </div>
                    <div className="md:col-span-4 text-lg font-semibold">{t.title}</div>
                    <div className="md:col-span-6 text-ink-soft leading-relaxed">{t.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page">
          <SectionHeader eyebrow="Expertise & Capabilities" index="02" title="The operating principles." />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-2xl overflow-hidden">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="bg-background p-8 h-full">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary mb-6">
                    <p.Icon className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <h3 className="text-lg font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What 23 Years Have Taught Me */}
      <section className="py-24 border-t border-border bg-surface">
        <div className="container-page">
          <SectionHeader
            eyebrow="What 23 Years Have Taught Me"
            index="03"
            title="Eight principles shaped by two decades on site."
            description="Lessons drawn from luxury residences, perimeter grids, command centres, auditoriums, home cinemas and intelligent buildings — distilled into the way every brief is approached today."
          />
          <div className="mt-14 grid md:grid-cols-2 gap-4">
            {LESSONS.map((l, i) => (
              <Reveal key={l.n} delay={i * 0.04}>
                <div className="group h-full rounded-2xl border border-border bg-background p-7 hover:border-border-strong transition-colors">
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-xs text-primary">{l.n}</span>
                    <h3 className="text-lg font-semibold tracking-tight">{l.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed pl-8">{l.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-border">
        <div className="container-page">
          <Link to="/contact" className="tap-target inline-flex items-center gap-2 text-primary font-medium">
            Let's Talk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <ConsultingLinks intro="Arif Khan's consulting work across India is described in more detail here:" />

      <section className="py-20 border-t border-border">
        <div className="container-page max-w-4xl">
          <AuthorCard eyebrow="Identity" />
        </div>
      </section>
    </PageShell>
  );
}
